import React, { useEffect, useState } from 'react';
import { Form, Button, Modal, Container, Row, Col, Card, InputGroup } from 'react-bootstrap';
import { FaCheck, FaPlus } from 'react-icons/fa';
import './EditPage.css';
import Editor from '../../../Utilities/Editor';
import axios from '../../../interseptor/axiosConf';
import { useNavigate, useParams } from 'react-router-dom';
import ToastrNotifi from '../../../Utilities/ToastrNotifi';
import { usePageData, useUpdatePage } from '../../../hooks/usePageData';
import { useAddTag, useTagsData } from '../../../hooks/useTagsData';
import GlobleSpinner from '../../../Utilities/GlobleSpinner';

export default function EditPage() {
    const { page_id } = useParams();
    const { isLoading: pageDataLoading, data: pageData, isSuccess: isPageDataSuccess } = usePageData(true, page_id);
    const { isLoading: tagDataLoading, data: tagsData, isSuccess: isTagDataSuccess, refetch: TagsRefetch } = useTagsData();
    const { mutate: addTag } = useAddTag();
    const { mutate: updatePage } = useUpdatePage();
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [newTag, setNewTag] = useState('');
    const [editorContent, setEditorContent] = useState('');
    const [pageName, setPageName] = useState('');
    const [initialContent, setinitialContent] = useState('');
    const [errors, setErrors] = useState({});

    const navigate = useNavigate();

    useEffect(() => {
        if (isPageDataSuccess && isTagDataSuccess) {
            const tagIds = pageData?.content.tags?.map(tag => tag.id) || [];
            setTags(tagsData);
            setSelectedTags(tagIds);
            setPageName(pageData?.content.title);
            setEditorContent(pageData?.content?.content?.content);
            setinitialContent(pageData?.content?.content?.content);
        }
    }, [isPageDataSuccess, isTagDataSuccess, tagsData, pageData]);

    const handleAddCategory = async () => {
        try {
            const newTagData = { tagname: newTag };
            addTag(newTagData);
            TagsRefetch();
            setShowModal(false);
        } catch (error) {
            console.error('Error creating tag:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const requestData = {
            pageName,
            selectedTags,
            editorContent,
            page_id: pageData?.content.id,
        };

        updatePage(requestData,
            {
                onError: (error) => {
                    if (error.response && error.response.data && error.response.data.errors) {
                        setErrors(error.response.data.errors);
                        const errorMessages = Object.values(error.response.data.errors).flat();
                        errorMessages.forEach(message => ToastrNotifi.warningToast(message));
                    } else {
                        console.error('An unexpected error occurred:', error);
                        ToastrNotifi.errorToast("An unexpected error occurred. Please try again.");
                    }
                },
                onSuccess: (response) => {
                    ToastrNotifi.successToast("Page updated successfully!");
                    navigate(-1);
                },
            })
    };

    const handleCategoryClick = (categoryId) => {
        setSelectedTags(prev =>
            prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
        );
    };

    const findTags = (query) => {
        if (query) {
            const regex = new RegExp(query, 'i');
            setTags(tagsData.filter(tag => regex.test(tag.name)));
        } else {
            setTags(tagsData);  // Reset to all tags
        }
    };

    return (
        <>
            {!pageDataLoading && !tagDataLoading ? (
                <Container fluid>
                    <Row className="justify-content-center">
                        <Col md={12}>
                            <Card className="p-4 shadow-sm">
                                <h3 className="mb-4"><span className='h4'>Edit Page/</span> {pageData?.content?.title} </h3>
                                <Form onSubmit={handleSubmit}>
                                    {/* Page Name */}
                                    <Form.Group controlId="pageName" className="mb-3">
                                        <Form.Label>Page Name
                                            {errors.pageName ? (
                                                <div className="text-danger error-text">**{errors.pageName}</div>
                                            ) : (null)}
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter page name"
                                            value={pageName}
                                            onChange={(e) => setPageName(e.target.value)}
                                            required
                                        />
                                    </Form.Group>

                                    {/* Category List with Add Option */}
                                    <Card>
                                        <Card.Body>
                                            <Form.Group controlId="categoryList" className="mb-3">
                                                <div className='d-flex justify-content-between align-items-center'>
                                                    <Form.Label>Tags
                                                        {errors.selectedTags ? (
                                                            <div className="text-danger error-text">**{errors.selectedTags}</div>
                                                        ) : (null)}
                                                    </Form.Label>
                                                    <Button
                                                        variant="outline-secondary btn-sm"
                                                        onClick={() => setShowModal(true)}
                                                        style={{ fontSize: '10px' }}
                                                    >
                                                        <FaPlus /> Add New Tag
                                                    </Button>
                                                </div>

                                                {/* Search Bar for tags */}
                                                <Form.Group controlId="searchCategory" className="mb-3">
                                                    <Col sm={12} md={3}>
                                                        <InputGroup className="input-group-sm">
                                                            <Form.Control
                                                                type="text"
                                                                placeholder="Search tags..."
                                                                onChange={(e) => findTags(e.target.value)}
                                                            />
                                                        </InputGroup>
                                                    </Col>
                                                </Form.Group>
                                                <div className="scrollable-container">
                                                    <div className="category-list">
                                                        {tags.length ==0 ?(
                                                            <p>No tags found</p>
                                                        ):(
                                                            <>
                                                            {[...tags].sort((a, b) => {
                                                                const aSelected = selectedTags.includes(a.id);
                                                                const bSelected = selectedTags.includes(b.id);
                                                                return (aSelected === bSelected) ? 0 : aSelected ? -1 : 1;
                                                            }).map((category) => (
                                                                <Button
                                                                    key={category.id}
                                                                    variant={selectedTags.includes(category.id) ? 'primary' : 'outline-secondary'}
                                                                    className="me-2 mb-2 btn-sm"
                                                                    onClick={() => handleCategoryClick(category.id)}
                                                                >
                                                                    {selectedTags.includes(category.id) ? <FaCheck className='mx-1' /> : <FaPlus className='mx-1' />}{category.name}
                                                                </Button>
                                                            ))}
                                                            </>
                                                        )}

                                                      
                                                    </div>
                                                </div>
                                            </Form.Group>
                                        </Card.Body>
                                    </Card>

                                    {/* Editor for Page Content */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Page Content
                                            {errors.editorContent ? (
                                                <div className="text-danger error-text">**{errors.editorContent}</div>
                                            ) : (null)}
                                        </Form.Label>
                                        {initialContent ? (
                                            <Editor
                                                editorContent={editorContent}
                                                setEditorContent={setEditorContent}
                                                initialContents={initialContent}
                                            />
                                        ) : (null)}

                                    </Form.Group>

                                    {/* Submit Button */}
                                    <div className="text-center">
                                        <Button variant="primary" type="submit" className="px-4">
                                            Update Page
                                        </Button>
                                    </div>
                                </Form>
                            </Card>
                        </Col>
                    </Row >

                    {/* Modal to Add New Category */}
                    <Modal show={showModal} onHide={() => setShowModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>Add New Category</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group controlId="newCategory">
                                <Form.Label>Category Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter new category"
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowModal(false)}>
                                Close
                            </Button>
                            <Button variant="primary" onClick={handleAddCategory}>
                                Add Category
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Container>
            ) : (
                <GlobleSpinner />
            )}

        </>
    );
}
